import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createOrgAdmin,
  fetchAdminOrganizationBySlug,
  fetchCurrentUserInfo,
} from "../api.js";
import { Button } from "../components/ui/button.jsx";
import { ArrowLeft } from "lucide-react";

export default function AdminCreatePage() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const me = await fetchCurrentUserInfo();
        if (!mounted) return;
        if (me.role !== "superadmin") {
          setError("Apenas superadmin pode criar admins.");
          setLoading(false);
          return;
        }
        const orgData = await fetchAdminOrganizationBySlug(orgSlug);
        if (!mounted) return;
        setOrg(orgData);
      } catch (err) {
        setError(err.response?.data?.detail || "Erro ao carregar organizacao");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [orgSlug]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!org) return;
    setError(null);
    setSubmitting(true);
    try {
      await createOrgAdmin(org.id, {
        email: form.email,
        password: form.password,
        name: form.name?.trim() || null,
      });
      navigate(`/admin/${orgSlug}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar admin");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <p className="text-white text-center">A carregar...</p>
      </main>
    );
  }

  if (!org || error) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg max-w-2xl mx-auto">
          {error || "Organizacao nao encontrada"}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(`/admin/${orgSlug}`)}
          className="flex items-center gap-2 text-white mb-6 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gold mb-3">
            Novo admin
          </h1>
          <p className="text-muted text-lg">Organizacao: {org.name}</p>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="admin-email" className="block text-sm font-medium text-white">
                Email *
              </label>
              <input
                id="admin-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="admin-password" className="block text-sm font-medium text-white">
                Password *
              </label>
              <input
                id="admin-password"
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="admin-name" className="block text-sm font-medium text-white">
                Nome (opcional)
              </label>
              <input
                id="admin-name"
                value={form.name}
                onChange={set("name")}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={submitting}>
                {submitting ? "A criar..." : "Criar admin"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/admin/${orgSlug}`)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
